import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { AnimatedCard } from "../ActionPlan/styles";
import { Skeleton } from "components/ui/skeleton";
import {
  SG_GET_CHAT_GPT_SUMMARY,
  CLEAR_CHAT_GPT_SUMMARY,
} from "constants/actions";

const QuestionText = styled.h3`
  font-family: 'Geist Variable', 'Geist', sans-serif;
  font-weight: 400;
  font-size: clamp(15px, 2.2vw, 20px);
  text-align: left;
  margin: 0 0 10px 0;
  color: oklch(.145 0 0);
`;

const PromptNote = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 6px;
`;

const LoaderWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 0;
  min-height: 120px;
`;

const FollowUpQuestions = forwardRef(function FollowUpQuestions({ themeColors, actions, statuses, notes, onComplete }, ref) {
  const dispatch = useDispatch();
  const { chatGptState } = useSelector(
    (state) => ({ chatGptState: state.chatgpt }),
    shallowEqual
  );

  const [currentIndex, setCurrentIndex] = useState(0); // 0..1 questions
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef("");
  const loaderStartRef = useRef(0);
  const MIN_LOADER_MS = 400;

  const contextString = useMemo(() => {
    try {
      const data = actions.map((a) => ({
        id: a.id,
        title: a.title,
        description: a.description,
        status: statuses[a.id] || "",
        note: notes[a.id] || "",
      }));
      return JSON.stringify(data);
    } catch (e) {
      return "[]";
    }
  }, [actions, statuses, notes]);

  function buildQuestionPrompt(index) {
    const history = answers
      .map((a, i) => `Q${i + 1}: ${questions[i]}\nA${i + 1}: ${a}`)
      .join("\n\n");

    return `You are helping a manager complete a short weekly check-in based on their existing action plan. Use the following JSON with actions, current statuses, and notes as context (do not repeat it back):\n---\n${contextString}\n---\n\nGenerate exactly ONE simple, focused follow-up question ${index === 0 ? "to understand any blockers or risks" : "to gather feedback on progress or needed support"}.\n- Avoid jargon and keep it brief.\n- Ask about specifics that help us unblock or sustain progress.\n- Do not include preamble. Return only the question text.\n\nConversation so far (if any):\n${history || "(no prior answers)"}`;
  }

  function requestQuestion(index) {
    setLoading(true);
    dispatch({ type: CLEAR_CHAT_GPT_SUMMARY });
    tokenRef.current = uuidv4();
    dispatch({
      type: SG_GET_CHAT_GPT_SUMMARY,
      payload: { payload: buildQuestionPrompt(index), token: tokenRef.current },
    });
    loaderStartRef.current = Date.now();
  }

  useEffect(() => {
    requestQuestion(0);
  }, []);

  useEffect(() => {
    const responseText = chatGptState?.chat_gpt_summary?.response;
    if (!responseText) return;
    if (loading) {
      const q = (responseText || "").trim();
      const elapsed = Date.now() - (loaderStartRef.current || 0);
      const remaining = Math.max(0, MIN_LOADER_MS - elapsed);
      const apply = () => {
        setQuestions((prev) => {
          const updated = [...prev];
          updated[currentIndex] = q;
          return updated;
        });
        setLoading(false);
      };
      if (remaining > 0) {
        const t = setTimeout(apply, remaining);
        return () => clearTimeout(t);
      } else {
        apply();
      }
    }
  }, [chatGptState, loading, currentIndex]);

  function submit(value) {
    const answer = String(value || "").trim();
    if (!answer || loading) return;
    setAnswers((prev) => {
      const updated = [...prev];
      updated[currentIndex] = answer;
      return updated;
    });
    if (currentIndex >= 1) {
      onComplete?.({ questions, answers: [...answers, answer] });
    } else {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      requestQuestion(nextIndex);
    }
  }

  useImperativeHandle(ref, () => ({
    submitAnswer: submit,
  }));

  const questionText = questions[currentIndex] || "";
  const isLoading = loading || !questionText;

  return (
    <AnimatedCard isFadingOut={false}>
      {(isLoading) ? (
        <LoaderWrap>
          <Skeleton style={{ height: 20, width: '70%', backgroundColor: '#e5e7eb', borderRadius: 6 }} />
          <Skeleton style={{ height: 16, width: '48%', backgroundColor: '#e5e7eb', borderRadius: 6 }} />
          <div style={{ marginTop: 6 }}>
            <Skeleton style={{ height: 14, width: '36%', backgroundColor: '#e5e7eb', borderRadius: 6 }} />
          </div>
        </LoaderWrap>
      ) : (
        <>
          <QuestionText>{questionText}</QuestionText>
          <PromptNote>Answer below and press Send.</PromptNote>
        </>
      )}
    </AnimatedCard>
  );
});

export default FollowUpQuestions;

